import { Button, buttonVariants } from "@car/ui/components/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@car/ui/components/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@car/ui/components/sheet";
import {
  ArrowLeft02Icon,
  Call02Icon,
  CarAlertIcon,
  Clock01Icon,
  Location01Icon,
  Menu01Icon,
  Message01Icon,
  Moon02Icon,
  Sun03Icon,
  UserCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useTheme } from "next-themes";
import { useState } from "react";

import { Image } from "@car/ui/components/image";
import { cn } from "@car/ui/lib/utils";

import { isConsultLink, openConsult } from "@/lib/consult";
import { SiteSearch } from "@/components/site-search";
import {
  headerMenu,
  linkAttrs,
  telHref,
  useIsCurrent,
  useSiteContent,
  type MenuLink,
  type SiteBanner,
} from "@/lib/content";
import { toFaDigits } from "@/lib/format";

const bannerClass =
  "flex items-center gap-4 overflow-hidden rounded-2xl border bg-primary/5 px-5 py-3 text-sm font-bold";

/** بنرهای فعال یک جایگاه سایت (از مدیریت محتوا)؛ بدون بنر چیزی رندر نمی‌شود. بنر بی‌لینک فقط نمایش است. */
export function BannerStrip({ banners }: { banners: SiteBanner[] }) {
  if (banners.length === 0) return null;
  return (
    <div className="grid gap-2">
      {banners.map((banner) => {
        const body = (
          <>
            {banner.image ? (
              <Image
                src={banner.image}
                alt=""
                width={80}
                height={48}
                className="h-12 w-20 shrink-0 rounded-lg object-cover"
              />
            ) : null}
            {banner.title}
          </>
        );
        return banner.href ? (
          <a key={banner.id} href={banner.href} className={cn(bannerClass, "hover:bg-primary/10")}>
            {body}
          </a>
        ) : (
          <div key={banner.id} className={bannerClass}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/**
 * سربرگ سایت در یک ردیف: نشان، چهار مقصد واقعی (دو گروه منوی مدیریت محتوا، پیوندهای کنارشان و
 * «تماس و شعب» که تلفن‌ها و ساعت‌ها را یک‌جا دارد)، و سه کار: جستجوی همان‌جا، حساب و مشاوره‌ی
 * همان‌جا. پوسته در پاورقی و منوی موبایل است.
 *
 * overlay: نسخه‌ی صفحه‌ی اصلی؛ ثابت (fixed) و بالای هیرو شفاف با متن یخی است و پس از هیرو
 * با کلاس is-scrolled (که حرکتِ صفحه می‌زند) به حالت عادی برمی‌گردد. استایلش در index.css است.
 */
export function SiteHeader({ overlay = false }: { overlay?: boolean }) {
  const content = useSiteContent();
  const menu = headerMenu(content.headerMenu);
  const isCurrent = useIsCurrent();
  // مشاوره دکمه‌ی خودش را دارد؛ پیوند تکراری‌اش در منو نمی‌آید
  const links = menu.links.filter((link) => !isConsultLink(link));
  return (
    <header
      className={cn(
        "top-0 z-40 border-b border-border/80 bg-background/95 backdrop-blur-xl",
        overlay ? "home-header fixed inset-x-0" : "sticky",
      )}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-2 px-4 sm:px-6 lg:h-[72px] lg:gap-6">
        <a
          href="/"
          className="flex shrink-0 items-center gap-2 rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          aria-label="تابان خودرو، صفحه اصلی"
        >
          <img
            src="/logo.webp"
            alt=""
            width={320}
            height={227}
            data-logo
            className="h-10 w-auto lg:h-11 dark:brightness-0 dark:invert"
          />
          <span className="text-lg font-black">تابان خودرو</span>
        </a>

        <NavigationMenu className="hidden lg:flex" align="start" aria-label="منوی اصلی">
          <NavigationMenuList>
            {menu.groups.map((group, index) => (
              <NavigationMenuItem key={index}>
                <NavigationMenuTrigger className="whitespace-nowrap">
                  {group.title}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="w-[30rem]">
                  {group.note ? (
                    <p className="px-3 pt-2 text-xs text-muted-foreground">{group.note}</p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {group.links
                      .filter((link) => !isConsultLink(link))
                      .map((link, linkIndex) => (
                        <NavigationMenuLink
                          key={linkIndex}
                          {...linkAttrs(link)}
                          active={isCurrent(link)}
                          className="min-h-11"
                        >
                          {link.label}
                        </NavigationMenuLink>
                      ))}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            ))}
            {links.map((link, index) => (
              <NavigationMenuItem key={`link-${index}`}>
                <NavigationMenuLink
                  {...linkAttrs(link)}
                  active={isCurrent(link)}
                  className="min-h-11 px-4 whitespace-nowrap"
                >
                  {link.label}
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
            <NavigationMenuItem>
              <NavigationMenuTrigger className="whitespace-nowrap">
                تماس و شعب
              </NavigationMenuTrigger>
              <NavigationMenuContent className="w-[22rem]">
                <ContactPanel />
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        <div className="ms-auto flex items-center gap-1.5 sm:gap-2">
          <SiteSearch />
          <a
            href="/panel"
            data-cta="outline"
            aria-label="حساب کاربری"
            title="حساب کاربری"
            className={cn(
              buttonVariants({ variant: "ghost", size: "icon-lg" }),
              "hidden md:inline-flex xl:w-auto xl:gap-2 xl:px-3",
            )}
          >
            <HugeiconsIcon icon={UserCircleIcon} strokeWidth={1.8} />
            <span className="hidden xl:inline" aria-hidden="true">
              حساب کاربری
            </span>
          </a>
          <Button
            size="lg"
            data-cta="primary"
            className="hidden md:inline-flex"
            aria-haspopup="dialog"
            onClick={openConsult}
          >
            مشاوره رایگان
          </Button>
          {/* در موبایل تماس با فروش یک لمس فاصله دارد؛ امداد جاده‌ای با برچسب خودش در منو است */}
          <SalesCall className="md:hidden" />
          <div className="lg:hidden">
            <MobileMenu />
          </div>
        </div>
      </div>
    </header>
  );
}

/** تلفن فروش (بلوک «تماس»)؛ بی‌شماره به صفحه «تماس با ما» می‌رود که تلفن شعب را دارد */
export const salesHref = (salesPhone: string) => (salesPhone ? telHref(salesPhone) : "/contact");

function SalesCall({ className }: { className?: string }) {
  const { contact } = useSiteContent();
  const label = contact.salesPhone
    ? `تماس با فروش: ${toFaDigits(contact.salesPhone)}`
    : "تماس با ما";
  return (
    <a
      href={salesHref(contact.salesPhone)}
      className={cn(buttonVariants({ variant: "ghost", size: "icon-lg" }), className)}
      aria-label={label}
      title={label}
      data-cta="outline"
    >
      <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
    </a>
  );
}

/**
 * تلفن‌ها و ساعت‌های کاری یک‌جا (به‌جای نوار بالای سربرگ): فروش، امداد جاده‌ای و پیوند
 * «نشانی و تلفن همه شعب». در منوی دسکتاپ و منوی موبایل همین بلوک می‌آید.
 */
function ContactPanel({ className }: { className?: string }) {
  const { contact } = useSiteContent();
  return (
    <div className={cn("grid gap-1 p-2", className)}>
      {contact.salesPhone ? (
        <a
          href={telHref(contact.salesPhone)}
          className="grid gap-0.5 rounded-lg p-3 outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} className="size-4" />
            تلفن فروش
          </span>
          <span dir="ltr" className="text-end text-lg font-black tabular-nums">
            {toFaDigits(contact.salesPhone)}
          </span>
        </a>
      ) : null}
      {contact.salesHours || contact.serviceHours ? (
        <div className="grid gap-1.5 px-3 py-2 text-xs text-muted-foreground">
          {[contact.salesHours, contact.serviceHours].filter(Boolean).map((hours) => (
            <p key={hours} className="flex items-center gap-2">
              <HugeiconsIcon icon={Clock01Icon} strokeWidth={1.8} className="size-4 shrink-0" />
              {hours}
            </p>
          ))}
        </div>
      ) : null}
      {contact.hotlinePhone ? (
        <a
          href={telHref(contact.hotlinePhone)}
          className="grid gap-0.5 rounded-lg p-3 outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <span className="flex items-center gap-2 text-xs font-bold text-destructive">
            <HugeiconsIcon icon={CarAlertIcon} strokeWidth={1.8} className="size-4" />
            {contact.hotlineLabel}
          </span>
          <span dir="ltr" className="text-end text-lg font-black tabular-nums">
            {toFaDigits(contact.hotlinePhone)}
          </span>
        </a>
      ) : null}
      <a
        href="/contact"
        className="mt-1 flex items-center gap-2 rounded-lg border-t px-3 pt-3 pb-2 text-sm font-bold text-primary outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <HugeiconsIcon icon={Location01Icon} strokeWidth={1.8} className="size-4" />
        نشانی، ساعت کاری و تلفن همه شعب
        <HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={1.8} className="ms-auto size-4" />
      </a>
    </div>
  );
}

/** کلید پوسته‌ی روشن و تیره؛ در پاورقی و منوی موبایل (برچسب‌دار در منو) */
export function ThemeToggle({
  withLabel = false,
  className,
}: {
  withLabel?: boolean;
  className?: string;
}) {
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const label = dark ? "پوسته روشن" : "پوسته تیره";
  return (
    <Button
      variant="ghost"
      size={withLabel ? "lg" : "icon-lg"}
      aria-label={label}
      title={label}
      className={className}
      onClick={() => setTheme(dark ? "light" : "dark")}
    >
      <HugeiconsIcon icon={Moon02Icon} strokeWidth={1.8} className="size-5 dark:hidden" />
      <HugeiconsIcon icon={Sun03Icon} strokeWidth={1.8} className="hidden size-5 dark:block" />
      {withLabel ? <span aria-hidden="true">{label}</span> : null}
    </Button>
  );
}

/** پیوند منو؛ پیوند مشاوره به‌جای پرش، فرم را همان‌جا باز می‌کند */
export function MenuEntry({
  link,
  current,
  className,
  onNavigate,
}: {
  link: MenuLink;
  current: boolean;
  className?: string;
  onNavigate?: () => void;
}) {
  if (isConsultLink(link)) {
    return (
      <button
        type="button"
        aria-haspopup="dialog"
        className={cn("text-start", className)}
        onClick={() => {
          onNavigate?.();
          openConsult();
        }}
      >
        {link.label}
      </button>
    );
  }
  return (
    <a
      {...linkAttrs(link)}
      aria-current={current ? "page" : undefined}
      className={className}
      onClick={onNavigate}
    >
      {link.label}
    </a>
  );
}

function MobileMenu() {
  const content = useSiteContent();
  const { contact } = content;
  const { mobile } = headerMenu(content.headerMenu);
  const isCurrent = useIsCurrent();
  // پیوند یا دکمه‌ی مشاوره اول منو را می‌بندد تا دیالوگ روی منو نیفتد
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        render={
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="باز کردن منو"
            title="منو"
            data-cta="outline"
          />
        }
      >
        <HugeiconsIcon icon={Menu01Icon} strokeWidth={1.8} />
      </SheetTrigger>
      <SheetContent side="right" className="w-[88%] max-w-sm gap-0" lang="fa" dir="rtl">
        <SheetHeader className="border-b">
          <SheetTitle className="text-lg font-black">تابان خودرو</SheetTitle>
          <SheetDescription>{contact.menuSubtitle}</SheetDescription>
        </SheetHeader>
        <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto overscroll-contain py-3">
          <nav className="grid gap-0.5 px-3" aria-label="منوی موبایل">
            {mobile.map((link, index) => {
              const current = isCurrent(link);
              return (
                <MenuEntry
                  key={index}
                  link={link}
                  current={current}
                  onNavigate={close}
                  className={cn(
                    "rounded-xl px-3 py-3 font-bold break-words outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring",
                    current && "bg-muted text-primary",
                  )}
                />
              );
            })}
          </nav>
          <div className="mx-3 rounded-2xl border">
            <ContactPanel className="p-1" />
          </div>
        </div>
        <div className="grid gap-2 border-t p-4">
          <Button
            size="lg"
            aria-haspopup="dialog"
            onClick={() => {
              close();
              openConsult();
            }}
          >
            <HugeiconsIcon icon={Message01Icon} strokeWidth={1.8} />
            مشاوره رایگان
          </Button>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <a className={buttonVariants({ variant: "outline", size: "lg" })} href="/panel">
              <HugeiconsIcon icon={UserCircleIcon} strokeWidth={1.8} />
              حساب کاربری
            </a>
            <ThemeToggle withLabel className="border" />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
